import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { authClient } from "@/lib/auth-client";
import { ArrowRight } from "lucide-react";

const DEFAULT_AUTH_TIMEOUT_MS = 10_000;
const DEFAULT_AUTH_RETRY_COUNT = 1;
const DEFAULT_AUTH_RETRY_DELAY_MS = 350;
const parsedTimeout = Number(import.meta.env?.VITE_AUTH_TIMEOUT_MS ?? DEFAULT_AUTH_TIMEOUT_MS);
const parsedRetryCount = Number(import.meta.env?.VITE_AUTH_RETRY_COUNT ?? DEFAULT_AUTH_RETRY_COUNT);
const parsedRetryDelay = Number(import.meta.env?.VITE_AUTH_RETRY_DELAY_MS ?? DEFAULT_AUTH_RETRY_DELAY_MS);
const AUTH_TIMEOUT_MS = Number.isFinite(parsedTimeout) && parsedTimeout > 0 ? parsedTimeout : DEFAULT_AUTH_TIMEOUT_MS;
const AUTH_RETRY_COUNT =
  Number.isInteger(parsedRetryCount) && parsedRetryCount >= 0 ? parsedRetryCount : DEFAULT_AUTH_RETRY_COUNT;
const AUTH_RETRY_DELAY_MS =
  Number.isFinite(parsedRetryDelay) && parsedRetryDelay > 0 ? parsedRetryDelay : DEFAULT_AUTH_RETRY_DELAY_MS;
const TIMEOUT_ERROR = "SIGN_IN_TIMEOUT";
const TRANSIENT_AUTH_ERROR = "Temporary server issue. Please try again.";
const TRANSIENT_AUTH_STATUS = 503;

type SignInResult = Awaited<ReturnType<typeof authClient.signIn.email>>;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(TIMEOUT_ERROR));
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId) clearTimeout(timeoutId);
  });
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isTransientError(status: number | undefined) {
  return status === TRANSIENT_AUTH_STATUS;
}

function getSignInErrorMessage(status: number | undefined, message: string | undefined) {
  if (isTransientError(status)) return TRANSIENT_AUTH_ERROR;
  return message ?? "Sign in failed";
}

async function signInWithRetry(email: string, password: string): Promise<SignInResult> {
  let retryCount = 0;

  while (true) {
    const result = await withTimeout(authClient.signIn.email({ email, password }), AUTH_TIMEOUT_MS);

    if (!isTransientError(result.error?.status) || retryCount >= AUTH_RETRY_COUNT) {
      return result;
    }

    retryCount += 1;
    const jitterMs = Math.floor(Math.random() * 150);
    await sleep(AUTH_RETRY_DELAY_MS + jitterMs);
  }
}

function getSignInFallbackError(error: unknown) {
  if (error instanceof Error && error.message === TIMEOUT_ERROR) {
    return "Sign in timed out. Please check your connection and try again.";
  }

  return "Unable to sign in. Please check your connection and try again.";
}

export function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setError("");
    setLoading(true);

    try {
      const result = await signInWithRetry(email, password);
      if (result.error) {
        setError(getSignInErrorMessage(result.error.status, result.error.message));
        return;
      }
      navigate("/");
    } catch (err) {
      setError(getSignInFallbackError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      {/* Subtle grid background */}
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `linear-gradient(#0a0a0a 1px, transparent 1px), linear-gradient(90deg, #0a0a0a 1px, transparent 1px)`,
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative w-full max-w-[360px] px-4">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center">
          <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-foreground">
            <span className="text-[15px] font-bold text-background">T</span>
          </div>
          <h1 className="text-[15px] font-semibold tracking-tight">Miss T's Admin</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">Sign in to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label htmlFor="email" className="mb-1.5 block text-[13px] font-medium text-muted-foreground">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="admin@misstees.ca"
              className="flex h-10 w-full rounded-lg border bg-background px-3 text-[13px] shadow-sm transition-all placeholder:text-muted-foreground/50 focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
            />
          </div>
          <div>
            <label htmlFor="password" className="mb-1.5 block text-[13px] font-medium text-muted-foreground">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="flex h-10 w-full rounded-lg border bg-background px-3 text-[13px] shadow-sm transition-all placeholder:text-muted-foreground/50 focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
            />
          </div>

          {error && (
            <p className="text-[13px] text-red-600">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="group flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-foreground text-[13px] font-medium text-background shadow-sm transition-all hover:opacity-90 disabled:opacity-50"
          >
            {loading ? (
              <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-background border-t-transparent" />
            ) : (
              <>
                Continue
                <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
